import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';

export const useUser = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    console.log('🔍 Initializing user system...');
    
    // Get or create user ID for this session
    let userId = sessionStorage.getItem('chatroom-user-id');
    if (!userId) {
      userId = uuidv4();
      sessionStorage.setItem('chatroom-user-id', userId);
      console.log('🆕 Created new user ID:', userId);
    } else {
      console.log('🔄 Using existing user ID:', userId);
    }

    // Get saved nickname
    const savedNickname = sessionStorage.getItem('chatroom-nickname');
    console.log('💾 Saved nickname:', savedNickname);

    // 確保不是測試用戶或有問題的用戶
    if (savedNickname === 'Test User' || savedNickname === '你好') {
      console.log('⚠️ Removing problematic nickname:', savedNickname);
      sessionStorage.removeItem('chatroom-nickname');
      setUser({ id: userId, nickname: null });
    } else {
      setUser({ id: userId, nickname: savedNickname || null });
    }
  }, []);

  const checkNicknameAvailability = async (nickname) => {
    try {
      const trimmedNickname = nickname.trim();
      console.log('🔍 Checking nickname availability:', trimmedNickname);
      
      // 禁止使用測試用戶名
      if (trimmedNickname === 'Test User' || trimmedNickname === '你好') {
        console.log('❌ Blocked problematic nickname:', trimmedNickname);
        return false;
      }

      const { data, error } = await supabase
        .from('online_users')
        .select('nickname')
        .eq('nickname', trimmedNickname);

      if (error) {
        console.error('❌ Error checking nickname:', error);
        return false;
      }

      const available = data.length === 0;
      console.log('✅ Nickname availability check result:', { nickname: trimmedNickname, available });
      
      return available;
    } catch (error) {
      console.error('❌ Exception in nickname availability check:', error);
      return false;
    }
  };

  const setNickname = async (nickname) => {
    const trimmedNickname = nickname.trim();
    console.log('📝 Setting nickname:', trimmedNickname);
    
    // 禁止使用測試用戶名
    if (trimmedNickname === 'Test User' || trimmedNickname === '你好') {
      console.log('❌ Blocked problematic nickname:', trimmedNickname);
      throw new Error('NICKNAME_TAKEN');
    }

    // 檢查暱稱是否已被使用
    const isAvailable = await checkNicknameAvailability(trimmedNickname);
    if (!isAvailable) {
      console.log('❌ Nickname already taken:', trimmedNickname);
      throw new Error('NICKNAME_TAKEN');
    }

    sessionStorage.setItem('chatroom-nickname', trimmedNickname);
    setUser(prev => ({ ...prev, nickname: trimmedNickname }));
    console.log('✅ Nickname set successfully:', trimmedNickname);
    return true;
  };

  const clearUser = async () => {
    try {
      console.log('🧹 Clearing user data...');
      
      // 先從在線用戶中移除
      if (user?.id) {
        const { error } = await supabase
          .from('online_users')
          .delete()
          .eq('id', user.id);
        
        if (error) {
          console.error('❌ Error removing user from online list:', error);
        } else {
          console.log('✅ User removed from online list:', user.nickname);
        }
      }
    } catch (error) {
      console.warn('⚠️ Error removing user from online list:', error);
    }

    sessionStorage.removeItem('chatroom-nickname');
    sessionStorage.removeItem('chatroom-user-id');
    setUser(prev => ({ ...prev, nickname: null }));
    console.log('✅ User data cleared');
  };

  return { user, setNickname, clearUser, checkNicknameAvailability };
};